import { FunctionComponent, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Spinner, Checkbox, TreeView, TreeViewItem } from "@volvo/vcdk-react";
import { CheckboxChangeEvent } from "@volvo/vcdk";
import { useGetDealerClassesQuery } from '../../../store/InvoiceApiSlice';
import { DealerClass, AccountableType } from '../../../Types';

export const ClassTreeView: FunctionComponent<{
    classChangedCallback?: (classID: number, checked: boolean, typeList: AccountableType[]) => void,
    typeChangedCallback?: (type: AccountableType, checked: boolean) => void,
    ref: any
}> = forwardRef(({ classChangedCallback, typeChangedCallback }, ref) => {
    const {data: classList = [], isLoading, isSuccess, isError, error} = useGetDealerClassesQuery({});
    const [stateClassList, setStateClassList] = useState<DealerClass[]>([]);

    useImperativeHandle(ref, () => ({
        selectedAllClasses: (checked: boolean) => {
            setStateClassList(stateClassList.map((dealerClass: DealerClass) => ({
                ...dealerClass,
                typeList: dealerClass.typeList.map((type: AccountableType) => ({ ...type, checked })),
                checked
            })));
        },
        getAllClasses: () => (classList)
    }));

    useEffect(() => {
        if(isSuccess){
            setStateClassList(classList.map((dealerClass: DealerClass) => ({
                ...dealerClass,
                checked: false,
                typeList: dealerClass.typeList.map((type: AccountableType) => ({ ...type, checked: false }))
            })));
        }
    }, [isSuccess, classList]);

    const classChangedHandler = (e: CheckboxChangeEvent, classID: number, typeList: AccountableType[]) => {
        let checked = e.detail.checked;
        setStateClassList(stateClassList.map((dealerClass: DealerClass) => {
            if (dealerClass.dealerClassID === classID) dealerClass = {
                ...dealerClass,
                typeList: dealerClass.typeList.map((type: AccountableType) => ({ ...type, checked })),
                checked
            }
            return dealerClass;
        }));
        classChangedCallback?.(classID, checked, typeList);
    }

    const typeChangedHandler = (e: CheckboxChangeEvent, type: AccountableType) => {
        let checked = e.detail.checked;
        setStateClassList(stateClassList.map((dealerClass: DealerClass) => {
            if(dealerClass.dealerClassID === type.dealerClassID) {
                let allTypeChecked = checked, allTypeUnchecked = !checked;
                dealerClass = {
                    ...dealerClass,
                    typeList: dealerClass.typeList.map((at: AccountableType) => {
                        if(at.accountableTypeID === type.accountableTypeID) at = { ...at, checked }
                        if(allTypeChecked && !at.checked) allTypeChecked = false;
                        if(allTypeUnchecked && at.checked) allTypeUnchecked = false;
                        return at;
                    }),
                    checked: allTypeChecked ? true : (allTypeUnchecked ? false : undefined)
                }
            }
            return dealerClass;
        }));
        typeChangedCallback?.(type, checked);
    }

    if(isLoading) return <Spinner />
    return (
        <TreeView style={{ border: '1px solid #A9A9A9', height: 353, backgroundColor: '#FFFFFF', borderRadius: 4, padding: 8, overflowY: 'auto' }}>
            {stateClassList.map((dealerClass: DealerClass) => (
                <TreeViewItem key={`dealerClass-${dealerClass.dealerClassID}`}>
                    <Checkbox
                        checked={dealerClass.checked}
                        onChange={(e: CheckboxChangeEvent) => classChangedHandler(e, dealerClass.dealerClassID, dealerClass.typeList)}
                        label={`${dealerClass.name}-${dealerClass.description}`} />
                    {dealerClass.typeList.map((type: AccountableType) => (
                        <TreeViewItem key={`type-${type.accountableTypeID}`}>
                            <Checkbox checked={type.checked} onChange={(e: CheckboxChangeEvent) => typeChangedHandler(e, type)} label={`${type.name}-${type.description}`} />
                        </TreeViewItem>
                    ))}
                </TreeViewItem>
            ))}
        </TreeView>
    );    
});
